// src/Components/Common/Header.jsx
// Changes from previous version:
//   - Unauthenticated nav: 3 items only (For Business, Discover Local, Startups)
//   - Mobile unauthenticated: 3 icons instead of 5
//   - Authenticated Explore menu: 2 items (AI Platform, Discover Local)
//   - Removed all /landing/ai-tools and /landing/newbusiness references

import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Badge,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import SmsIcon from "@mui/icons-material/Sms";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import BusinessIcon from "@mui/icons-material/Business";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ExploreIcon from "@mui/icons-material/Explore";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useSelector, useDispatch } from "react-redux";
import { signOut } from "../../redux/slices/auth";

// ─── Nav data ─────────────────────────────────────────────────────────────────

// Authenticated app nav (unchanged)
const AUTH_NAV_LINKS = [
  { label: "Home",          to: "/home",          icon: <HomeIcon /> },
  { label: "My Network",    to: "/network",        icon: <PeopleIcon /> },
  { label: "Chat",          to: "/chat",           icon: <SmsIcon /> },
  { label: "Notifications", to: "/notifications",  icon: <NotificationsActiveIcon /> },
  { label: "Explore",       to: null,              icon: <ExploreIcon /> }, // opens menu
];

const AUTH_BOTTOM_NAV = [
  { label: "Home",          icon: <HomeIcon /> },
  { label: "Network",       icon: <PeopleIcon /> },
  { label: "Chat",          icon: <SmsIcon /> },
  { label: "Notifications", icon: <NotificationsActiveIcon /> },
  { label: "Explore",       icon: <ExploreIcon /> },
];

// Authenticated Explore menu — 2 items
const EXPLORE_MENU_ITEMS = [
  { label: "AI Platform",    to: "/landing/business", icon: <SmartToyIcon />, color: "#1976D2" },
  { label: "Discover Local", to: "/landing/users",    icon: <SearchIcon />,   color: "#4CAF50" },
];

// Unauthenticated nav — 3 items only, matches homepage B2B-first narrative
const UNAUTH_NAV_LINKS = [
  { label: "For Business",   to: "/landing/business", icon: <BusinessIcon />,   color: "#1976D2" },
  { label: "Discover Local", to: "/landing/users",    icon: <SearchIcon />,     color: "#4CAF50" },
  { label: "Startups",       to: "/landing/startups", icon: <RocketLaunchIcon />, color: "#9C27B0" },
];

// ─── Component ────────────────────────────────────────────────────────────────

const Header = ({ onSearch }) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [mobileOpen,          setMobileOpen]          = useState(false);
  const [exploreMenuAnchor,   setExploreMenuAnchor]   = useState(null);

  const { authStatus, signOut: authSignOut } = useAuthenticator(
    (context) => [context.authStatus, context.user]
  );
  const { loading } = useSelector((state) => state.auth);

  // ── Bottom nav active index ────────────────────────────────────────────────
  const getCurrentNavValue = () => {
    const path = location.pathname;
    if (path.startsWith("/home") || path === "/")    return 0;
    if (path.startsWith("/network"))                  return 1;
    if (path.startsWith("/chat"))                     return 2;
    if (path.startsWith("/notifications"))            return 3;
    if (path.startsWith("/landing/"))                 return 4;
    return false;
  };

  const [navValue, setNavValue] = useState(getCurrentNavValue());

  useEffect(() => {
    setNavValue(getCurrentNavValue());
  }, [location.pathname]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const onLogOut = () => {
    authSignOut();
    dispatch(signOut());
    navigate("/auth/signout");
  };

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const handleNavItemClick = (path) => {
    setMobileOpen(false);
    if (!path) return;
    if (path === "/home") {
      navigate(path, { state: { onboarding: false } });
    } else {
      navigate(path);
    }
  };

  const handleExploreMenuClose = () => setExploreMenuAnchor(null);

  const handleExploreMenuClick = (path) => {
    setExploreMenuAnchor(null);
    navigate(path);
  };

  const handleBottomNavChange = (event, newValue) => {
    setNavValue(newValue);
    if (newValue === 4) {
      setExploreMenuAnchor(event.currentTarget);
      return;
    }
    const routes = ["/home", "/network", "/chat", "/notifications"];
    if (routes[newValue]) handleNavItemClick(routes[newValue]);
  };

  // ── Drawer content ─────────────────────────────────────────────────────────
  const drawerContent = (
    <Box sx={{ width: "100vw", height: "100vh" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1, borderBottom: "1px solid #e0e0e0" }}>
        <Link to="/" style={{ textDecoration: "none" }}>
          <img src="/assets/revfer/skyblue.svg" alt="Revfer Logo" style={{ width: "100px", height: "50px" }} />
        </Link>
        <IconButton onClick={handleDrawerToggle}><CloseIcon /></IconButton>
      </Box>

      <List sx={{ pt: 0 }}>
        {(authStatus === "authenticated" ? AUTH_NAV_LINKS.filter(l => l.to) : UNAUTH_NAV_LINKS).map((link, i) => (
          <ListItem key={i} disablePadding>
            <ListItemButton
              onClick={() => handleNavItemClick(link.to)}
              sx={{ gap: 1, py: 1.5, "&:hover": { bgcolor: "rgba(25,118,210,0.04)" } }}
            >
              <Box sx={{ color: link.color || "#666" }}>{link.icon}</Box>
              <ListItemText primary={link.label} primaryTypographyProps={{ fontWeight: 500 }} />
            </ListItemButton>
          </ListItem>
        ))}

        {/* Explore sub-items in drawer for authenticated users */}
        {authStatus === "authenticated" && (
          <>
            <Divider sx={{ my: 0.5 }} />
            {EXPLORE_MENU_ITEMS.map((item, i) => (
              <ListItem key={i} disablePadding>
                <ListItemButton
                  onClick={() => handleNavItemClick(item.to)}
                  sx={{ gap: 1, py: 1.5, pl: 4, "&:hover": { bgcolor: `${item.color}0A` } }}
                >
                  <Box sx={{ color: item.color }}>{item.icon}</Box>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontWeight: 500, color: item.color, fontSize: 14 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </>
        )}

        {/* Unauthenticated CTA */}
        {authStatus !== "authenticated" && (
          <>
            <Divider sx={{ my: 2 }} />
            <ListItem sx={{ px: 2 }}>
              <Button
                fullWidth variant="contained" component={Link} to="/auth/signin"
                sx={{ textTransform: "none", bgcolor: "#1976D2", "&:hover": { bgcolor: "#1565C0" } }}
              >
                Get Started
              </Button>
            </ListItem>
          </>
        )}

        {/* Sign out */}
        {authStatus === "authenticated" && (
          <>
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => { onLogOut(); setMobileOpen(false); }}
                sx={{ gap: 1, color: "#d32f2f", "&:hover": { bgcolor: "rgba(211,47,47,0.04)" } }}
              >
                <ExitToAppIcon />
                <ListItemText primary="Sign Out" />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  // ── Unauthenticated desktop/tablet nav ─────────────────────────────────────
  const unauthDesktopNav = (
    <Box sx={{ display: "flex", alignItems: "center", gap: { md: 1, lg: 3 } }}>
      {/* Desktop: icon + label columns */}
      <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", gap: 3 }}>
        {UNAUTH_NAV_LINKS.map((link, i) => (
          <Box
            key={i}
            onClick={() => navigate(link.to)}
            sx={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer", transition: "all 0.2s", "&:hover": { transform: "translateY(-2px)" } }}
          >
            <IconButton size="small" sx={{ color: link.color, "&:hover": { color: link.color, bgcolor: `${link.color}12` } }}>
              {link.icon}
            </IconButton>
            <Typography variant="caption" sx={{ mt: "-5px", color: link.color, fontWeight: 500, fontSize: "0.7rem" }}>
              {link.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Tablet: text links */}
      <Box sx={{ display: { xs: "none", md: "flex", lg: "none" }, alignItems: "center", gap: 1 }}>
        {UNAUTH_NAV_LINKS.map((link, i) => (
          <Button
            key={i}
            component={Link}
            to={link.to}
            sx={{ textTransform: "none", color: link.color, fontWeight: 500, fontSize: "0.85rem", "&:hover": { bgcolor: `${link.color}0A` } }}
          >
            {link.label}
          </Button>
        ))}
      </Box>

      {/* Get Started CTA */}
      <Button
        variant="contained"
        component={Link}
        to="/auth/signin"
        sx={{ textTransform: "none", bgcolor: "#1976D2", "&:hover": { bgcolor: "#1565C0" }, boxShadow: "0 2px 8px rgba(25,118,210,0.3)", fontSize: { xs: "0.85rem", md: "0.9rem" }, px: { xs: 2, md: 3 } }}
      >
        Get Started
      </Button>
    </Box>
  );

  if (loading) return null;

  return (
    <>
      {/* ── Mobile top bar for authenticated users ───────────────────────── */}
      {isMobile && authStatus === "authenticated" ? (
        <AppBar position="fixed" sx={{ backgroundColor: "white", boxShadow: "0px 1px 3px rgba(0,0,0,0.1)", zIndex: 1000 }}>
          <Toolbar sx={{ minHeight: "48px !important", px: 2 }}>
            <Link to="/" style={{ textDecoration: "none" }}>
              <img src="/assets/revfer/skyblue.svg" alt="Revfer" style={{ width: "80px", height: "32px" }} />
            </Link>
            <Box sx={{ flex: 1, textAlign: "center" }}>
              <Typography variant="subtitle1" sx={{ color: "#1976D2", fontWeight: 600, fontSize: "1rem" }}>
                {AUTH_BOTTOM_NAV[navValue]?.label || "Revfer"}
              </Typography>
            </Box>
            <IconButton onClick={onLogOut} size="small" sx={{ color: "#666", "&:hover": { color: "#d32f2f", bgcolor: "rgba(211,47,47,0.04)" } }}>
              <ExitToAppIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      ) : (
        // ── Desktop / mobile unauthenticated top bar ─────────────────────
        <AppBar position="fixed" sx={{ backgroundColor: "white", boxShadow: "0px 4px 8px rgba(0,0,0,0.1)", width: "100%" }}>
          <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
            <Link to="/" style={{ overflow: "hidden" }}>
              <img src="/assets/revfer/skyblue.svg" alt="Revfer Logo" style={{ width: "100px", height: "50px" }} />
            </Link>

            {/* Desktop nav */}
            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              {authStatus === "authenticated" ? (
                <Box display="flex" alignItems="center">
                  {AUTH_NAV_LINKS.map((link, i) => (
                    <Box key={i} sx={{ display: "flex", flexDirection: "column", alignItems: "center", mr: 4 }}>
                      <IconButton
                        onClick={(e) => {
                          if (link.label === "Explore") { setExploreMenuAnchor(e.currentTarget); }
                          else { handleNavItemClick(link.to); }
                        }}
                        sx={{
                          color: (location.pathname.startsWith(link.to || "__") ||
                                  (link.label === "Explore" && location.pathname.startsWith("/landing/")))
                            ? "#1976D2" : "#666",
                          "&:hover": { color: "#1976D2", bgcolor: "rgba(25,118,210,0.04)" },
                        }}
                      >
                        {link.icon}
                      </IconButton>
                      <Typography
                        variant="caption"
                        sx={{
                          mt: "-5px",
                          color: (location.pathname.startsWith(link.to || "__") ||
                                  (link.label === "Explore" && location.pathname.startsWith("/landing/")))
                            ? "#1976D2" : "#666",
                          fontWeight: (location.pathname.startsWith(link.to || "__") ||
                                       (link.label === "Explore" && location.pathname.startsWith("/landing/")))
                            ? 600 : 500,
                        }}
                      >
                        {link.label}
                      </Typography>
                    </Box>
                  ))}
                  {/* Sign out */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <IconButton onClick={onLogOut} sx={{ color: "#666", "&:hover": { color: "#d32f2f", bgcolor: "rgba(211,47,47,0.04)" } }}>
                      <ExitToAppIcon />
                    </IconButton>
                    <Typography variant="caption" sx={{ mt: "-5px", color: "#666", fontWeight: 500 }}>Sign Out</Typography>
                  </Box>
                </Box>
              ) : (
                unauthDesktopNav
              )}
            </Box>

            {/* Mobile right side */}
            {isMobile && authStatus !== "authenticated" ? (
              // ── 3 icons for unauthenticated mobile ─────────────────────
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {UNAUTH_NAV_LINKS.map((link, i) => (
                  <Box
                    key={i}
                    onClick={() => navigate(link.to)}
                    sx={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer", "&:active": { transform: "scale(0.9)" } }}
                  >
                    <IconButton size="small" sx={{ color: link.color, padding: "4px", "&:hover": { bgcolor: `${link.color}15` } }}>
                      {link.icon}
                    </IconButton>
                    <Typography variant="caption" sx={{ mt: "-2px", color: link.color, fontWeight: 500, fontSize: "0.6rem", lineHeight: 1, whiteSpace: "nowrap" }}>
                      {link.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              // Hamburger for authenticated mobile
              <IconButton
                sx={{ display: { xs: "flex", md: "none" } }}
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>
      )}

      {/* ── Bottom navigation — authenticated mobile only ────────────────── */}
      {isMobile && authStatus === "authenticated" && (
        <Paper sx={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000, borderTop: "1px solid #e0e0e0" }} elevation={3}>
          <BottomNavigation
            value={navValue}
            onChange={handleBottomNavChange}
            showLabels={false}
            sx={{
              height: 60,
              "& .MuiBottomNavigationAction-root": { minWidth: "auto", padding: "6px 0", "&.Mui-selected": { color: "#1976D2" } },
            }}
          >
            {AUTH_BOTTOM_NAV.map((item, i) => (
              <BottomNavigationAction
                key={i}
                icon={
                  i === 3 ? (
                    <Badge badgeContent={0} color="error" max={99}>{item.icon}</Badge>
                  ) : item.icon
                }
                sx={{ "& .MuiSvgIcon-root": { fontSize: "1.5rem" } }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}

      {/* ── Explore dropdown menu (desktop + mobile bottom nav) ──────────── */}
      <Menu
        anchorEl={exploreMenuAnchor}
        open={Boolean(exploreMenuAnchor)}
        onClose={handleExploreMenuClose}
        anchorOrigin={{ vertical: isMobile ? "top" : "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: isMobile ? "bottom" : "top", horizontal: "center" }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 2,
            minWidth: 200,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            border: "1px solid rgba(0,0,0,0.08)",
            mt: isMobile ? -1 : 0.5,
          },
        }}
      >
        {EXPLORE_MENU_ITEMS.map((item, i) => (
          <MenuItem
            key={i}
            onClick={() => handleExploreMenuClick(item.to)}
            sx={{ py: 1.5, "&:hover": { bgcolor: `${item.color}0F` } }}
          >
            <ListItemIcon sx={{ color: item.color, minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontWeight: 500, color: item.color }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* ── Drawer — authenticated mobile hamburger ──────────────────────── */}
      <Drawer
        anchor="top"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ display: { xs: "block", md: "none" } }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Header;